import {logError} from '../../../services/errors/errorReporting';
import {type CachedDataWithSize, type LongTermCache} from '../types';
import {logPerfAndErrors} from '../util';

/**
 * CacheApiCache is an implementation of the LongTermCache interface using the
 * Cache API available in modern browsers. This class provides methods to
 * interact with the browser's cache storage, allowing for storing, retrieving,
 * checking, and deleting cached data. It is designed to handle JSON-serializable
 * data and includes performance logging for cache operations.
 *
 * The Cache API is integrated with the browser's fetch API and stores cached
 * values as request/response pairs. More information can be found on MDN:
 * https://developer.mozilla.org/en-US/docs/Web/API/Cache
 */

const CONTENT_LENGTH_HEADER = 'cache-content-length';

export class CacheApiCache implements LongTermCache {
  private openedCache: Cache | null = null;
  private browserHasCache = true;

  constructor(
    public readonly cacheName: string = 'wandb-graphql-cache',
    public readonly cacheVersion: string = 'v1'
  ) {}

  private disable() {
    this.browserHasCache = false;
    this.openedCache = null;
  }

  private async put(request: Request, response: Response) {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    try {
      await cache.put(request, response);
    } catch (error) {
      logError(`Error calling cache.put: ${error}, caching disabled`);
      this.disable();
    }
  }

  private async delete(request: Request) {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    try {
      await cache.delete(request);
    } catch (error) {
      logError(`Error calling cache.delete: ${error}, caching disabled`);
      this.disable();
    }
  }

  private async match(request: Request) {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return null;
    }
    try {
      return await cache.match(request);
    } catch (error) {
      logError(`Error calling cache.match: ${error}, caching disabled`);
      this.disable();
      return null;
    }
  }

  async get<T>(
    key: string,
    namespace: string
  ): Promise<CachedDataWithSize<T> | null> {
    const cacheGet = async () => {
      const cache = await this.ensurePrepared();
      if (cache == null) {
        return null;
      }

      const request = new Request(key);
      const cachedResponse = await this.match(request);
      const contentLength = cachedResponse?.headers.get(CONTENT_LENGTH_HEADER);
      let size = contentLength ? parseInt(contentLength) : null;

      if (size === null || isNaN(size)) {
        size = null;
      }

      const cachedData: T | null = cachedResponse
        ? await cachedResponse.json()
        : null;
      return cachedData
        ? {data: cachedData, bytesReadFromCache: size ?? 0}
        : null;
    };
    const result = await logPerfAndErrors('cache.get', cacheGet, namespace);

    return result ?? null;
  }

  async hasKey(key: string): Promise<boolean> {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return false;
    }
    const request = new Request(key);
    const match = await this.match(request);
    return match != null;
  }

  async set<T>(key: string, data: T, namespace: string): Promise<void> {
    if (data == null) {
      return;
    }

    const cacheSet = async () => {
      const cache = await this.ensurePrepared();
      if (cache == null) {
        return;
      }

      const request = new Request(key);
      const serializedData = JSON.stringify(data);
      const headers = new Headers({
        [CONTENT_LENGTH_HEADER]: serializedData.length.toString(),
      });
      const responseToCache = new Response(serializedData, {headers});
      await this.put(request, responseToCache);
    };
    await logPerfAndErrors('cache.set', cacheSet, namespace);
  }

  async clear() {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    await caches.delete(this.getCacheHandle());
    this.openedCache = null;
  }

  async remove(key: string): Promise<void> {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    const request = new Request(key);
    await this.delete(request);
  }

  private getCacheHandle(): string {
    return `${this.cacheName}-${this.cacheVersion}`;
  }

  private async ensurePrepared(): Promise<Cache | null> {
    if (this.openedCache == null && this.browserHasCache) {
      try {
        this.openedCache = await caches.open(this.getCacheHandle());
      } catch (error) {
        logError(`Error opening cache: ${error}, caching disabled`);
        this.browserHasCache = false;
      }
    }
    return this.openedCache;
  }
}
